import {
  Ledger,
  LedgerRow,
  LedgerSection,
  type LedgerTone,
  type LedgerValueTone,
} from '@/components/patterns/ledger'
import { Typography } from '@/components/ui/typography'
import type { CashDrawerReceiptLine } from '@/lib/services/cash-drawers/cash-drawers.types'
import { formatCurrency, parseCurrency } from '@/lib/utils'
import { Check } from 'lucide-react'
import type { ComponentPropsWithoutRef } from 'react'

// The deposit line turns green when every (short)/over line is zero.
const DEPOSIT_LINE_KEY = 'actual_deposit'

interface ReceiptSection {
  key: string
  title: string | null
  lines: CashDrawerReceiptLine[]
}

/** Server-ordered receipt lines (CashDrawerReceipt), grouped under their headings. */
export function CashDrawerReceiptLines({
  lines,
  ...props
}: { lines: CashDrawerReceiptLine[] } & ComponentPropsWithoutRef<'div'>) {
  const differences = lines.filter((line) => line.format === 'difference')
  const balanced = differences.length > 0 && differences.every((line) => isZero(line.amount))

  return (
    <Ledger {...props}>
      {groupReceiptLines(lines).map((section) => (
        <LedgerSection key={section.key} title={section.title} divided>
          {section.lines.map((line) => (
            <CashDrawerReceiptRow
              key={line.key}
              line={line}
              balanced={balanced && line.key === DEPOSIT_LINE_KEY}
            />
          ))}
        </LedgerSection>
      ))}
    </Ledger>
  )
}

function CashDrawerReceiptRow({
  line,
  balanced,
}: {
  line: CashDrawerReceiptLine
  balanced: boolean
}) {
  const amount = formatReceiptAmount(line)

  return (
    <LedgerRow
      label={line.label}
      hint={line.hint ?? amount.hint}
      value={amount.text}
      valueTone={amount.tone}
      tone={balanced ? 'success' : amount.band}
      badge={balanced ? <BalancedBadge /> : null}
      emphasis={line.emphasis === 'heading' ? 'line' : line.emphasis}
      indent={line.indent}
      muted={line.indent && isZero(line.amount)}
    />
  )
}

export function groupReceiptLines(lines: CashDrawerReceiptLine[]): ReceiptSection[] {
  const sections: ReceiptSection[] = []

  for (const line of lines) {
    const current = sections.at(-1)

    if (line.emphasis === 'heading') {
      sections.push({ key: line.key, title: line.label, lines: [] })
    } else if (current) {
      current.lines.push(line)
    } else {
      sections.push({ key: `${line.key}-section`, title: null, lines: [line] })
    }
  }

  return sections
}

interface ReceiptAmount {
  text: string
  tone: LedgerValueTone
  /** Set when the amount is a difference that needs the florist's attention. */
  band?: LedgerTone
  hint?: string
}

export function formatReceiptAmount(line: CashDrawerReceiptLine): ReceiptAmount {
  const cents = Math.round(parseCurrency(line.amount) * 100)

  if (line.format === 'difference') return formatTenderDifference(cents)

  return { text: formatCurrency(cents / 100), tone: 'default' }
}

/**
 * Counted minus expected, in cents, in accounting's (short)/over form: shortages in parentheses.
 * A difference also gets a status band and plain words; parentheses alone are easy to miss.
 */
export function formatTenderDifference(cents: number): ReceiptAmount {
  const amount = formatCurrency(Math.abs(cents) / 100)

  if (cents === 0) return { text: amount, tone: 'success' }
  if (cents > 0) {
    return {
      text: amount,
      tone: 'warning',
      band: 'warning',
      hint: `Over — counted ${amount} more than expected`,
    }
  }

  return {
    text: `(${amount})`,
    tone: 'destructive',
    band: 'destructive',
    hint: `Short — counted ${amount} less than expected`,
  }
}

export function BalancedBadge() {
  return (
    <Typography
      as="span"
      variant="caption"
      tone="inherit"
      weight="semibold"
      className="inline-flex items-center gap-1"
    >
      <Check aria-hidden="true" className="size-3.5" />
      Balanced
    </Typography>
  )
}

function isZero(amount: string | null) {
  return Math.round(parseCurrency(amount) * 100) === 0
}
